import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, Cargando, ErrorApi, Vacio, formatFecha } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';
import { exportarCSV, type ColumnaTabla } from '../shared/exportar.ts';
import type { Solicitud } from '../shared/tipos.ts';

type PersonaConv = {
  convocatoria_id: string;
  persona_nombre: string;
  persona_apellido: string;
  categoria_nombre: string;
  estado: string;
  hora_inicio: string | null; // horario propio del puesto (null = el del evento)
  hora_fin: string | null;
};

type Fila = Solicitud & {
  salon_nombre: string;
  convocatorias_total: number;
  convocatorias_aceptadas: number;
  convocatorias_pendientes: number;
  convocatorias_rechazadas: number;
  personas: PersonaConv[];
};

function Icono({ d, className = 'h-4 w-4' }: { d: string; className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const ICONOS = {
  calendario: 'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5',
  reloj: 'M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z',
  lugar: 'M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z',
  recargar: 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99',
  descargar: 'M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3',
  cubiertos: 'M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007Z',
  ojo: 'M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
};

function chipEstado(s: Fila): { texto: string; clase: string } {
  if (s.estado === 'cancelada') return { texto: 'CANCELADA', clase: 'bg-red-100 text-estado-error' };
  if (s.estado === 'finalizada') return { texto: 'FINALIZADA', clase: 'bg-superficie-200 text-neutro-500' };
  if (s.estado === 'en_curso') return { texto: 'EN CURSO', clase: 'bg-blue-100 text-estado-info' };
  const { convocatorias_aceptadas: aceptadas, convocatorias_total: total } = s;
  if (total > 0 && aceptadas >= total) return { texto: 'CONFIRMADA', clase: 'bg-estado-ok/15 text-estado-ok' };
  if (total > 0) return { texto: `${aceptadas}/${total} CONFIRMADOS`, clase: 'bg-amber-100 text-amber-700' };
  return { texto: 'ENVIADA', clase: 'bg-superficie-200 text-neutro-500' };
}

function idCorto(id: string): string {
  return `#${id.slice(0, 8).toUpperCase()}`;
}

const COLUMNAS_EXPORT: ColumnaTabla[] = [
  { clave: 'fecha', etiqueta: 'Fecha', formato: 'fecha' },
  { clave: 'salon_nombre', etiqueta: 'Salón' },
  { clave: 'hora_inicio', etiqueta: 'Desde' },
  { clave: 'hora_fin', etiqueta: 'Hasta' },
  { clave: 'estado', etiqueta: 'Estado' },
  { clave: 'convocatorias_aceptadas', etiqueta: 'Confirmadas', formato: 'numero' },
  { clave: 'convocatorias_total', etiqueta: 'Pedidas', formato: 'numero' },
  { clave: 'personas_str', etiqueta: 'Personas' },
  { clave: 'id', etiqueta: 'ID' },
];

const FILTROS = [
  { valor: '', etiqueta: 'Todas' },
  { valor: 'pendiente', etiqueta: 'Pendientes' },
  { valor: 'confirmada', etiqueta: 'Confirmadas' },
  { valor: 'en_curso', etiqueta: 'En curso' },
];

type FiltroTiempo = 'todas' | 'hoy' | 'semana' | 'proxima';

const FILTROS_TIEMPO: Array<{ valor: FiltroTiempo; etiqueta: string }> = [
  { valor: 'hoy', etiqueta: 'Hoy' },
  { valor: 'semana', etiqueta: 'Esta semana' },
  { valor: 'proxima', etiqueta: 'Semana que viene' },
  { valor: 'todas', etiqueta: 'Todas las fechas' },
];

function lunesActual(): Date {
  const hoy = new Date();
  const dow = hoy.getDay() === 0 ? 7 : hoy.getDay();
  return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (dow - 1));
}

function pasaFiltroTiempo(fecha: string, f: FiltroTiempo): boolean {
  if (f === 'todas') return true;
  const d = new Date(`${fecha}T00:00:00`);
  const ahora = new Date();
  const hoy0 = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  if (f === 'hoy') return d.getTime() === hoy0.getTime();
  const lun = lunesActual();
  const lunProx = new Date(lun.getFullYear(), lun.getMonth(), lun.getDate() + 7);
  const lunSig = new Date(lun.getFullYear(), lun.getMonth(), lun.getDate() + 14);
  if (f === 'semana') return d >= lun && d < lunProx;
  return d >= lunProx && d < lunSig;
}

export function MisSolicitudesPage() {
  const { datos, cargando, error, recargar } = useApi<Fila[]>('/api/solicitudes');
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('');
  // Arranca en "Esta semana": el cliente mira de un vistazo si su semana está confirmada.
  const [filtroTiempo, setFiltroTiempo] = useState<FiltroTiempo>('semana');
  const [busqueda, setBusqueda] = useState('');

  const activas = datos?.filter(s => !['finalizada', 'cancelada'].includes(s.estado)) ?? [];

  const q = busqueda.trim().replace(/^#/, '').toLowerCase();
  const filtradas = activas.filter((s) => {
    if (!pasaFiltroTiempo(s.fecha, filtroTiempo)) return false;
    if (filtro === 'confirmada' && !(s.convocatorias_total > 0 && s.convocatorias_aceptadas >= s.convocatorias_total)) return false;
    if (filtro === 'pendiente' && !(s.convocatorias_total === 0 || s.convocatorias_aceptadas < s.convocatorias_total)) return false;
    if (filtro === 'en_curso' && s.estado !== 'en_curso') return false;
    if (q && !(
      s.id.toLowerCase().includes(q)
      || s.salon_nombre.toLowerCase().includes(q)
      || s.personas.some((p) => `${p.persona_nombre} ${p.persona_apellido}`.toLowerCase().includes(q))
    )) return false;
    return true;
  });

  const pendientes = activas.filter(s => s.convocatorias_total === 0 || s.convocatorias_aceptadas === 0).length;
  const confirmadas = activas.filter(s => s.convocatorias_total > 0 && s.convocatorias_aceptadas >= s.convocatorias_total).length;
  const enCurso = activas.filter(s => s.estado === 'en_curso').length;
  const totalPersonas = activas.reduce((a, s) => a + s.convocatorias_aceptadas, 0);

  function exportar() {
    const filas = filtradas.map((s) => ({
      ...s,
      estado: chipEstado(s).texto.toLowerCase(),
      personas_str: s.personas
        .filter((p) => p.estado === 'aceptada')
        .map((p) => `${p.persona_apellido}, ${p.persona_nombre} (${p.categoria_nombre})`)
        .join(' · '),
    }));
    exportarCSV(filas, COLUMNAS_EXPORT, `mis-solicitudes-${new Date().toISOString().slice(0, 10)}.csv`);
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-neutro-800">Mis solicitudes</h2>
          <p className="text-sm text-neutro-500 mt-0.5">Gestioná el personal para tus eventos</p>
        </div>
        <div className="flex gap-2">
          <button
            title="Recargar"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 transition-colors"
            onClick={recargar}
          >
            <Icono d={ICONOS.recargar} />
          </button>
          <button
            title="Exportar a Excel"
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100 transition-colors"
            onClick={exportar}
          >
            <Icono d={ICONOS.descargar} />
          </button>
        </div>
      </div>

      {/* Stats bento */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Pendientes', value: pendientes, color: 'text-estado-pendiente' },
          { label: 'Confirmadas', value: confirmadas, color: 'text-estado-ok' },
          { label: 'En curso', value: enCurso, color: 'text-estado-info' },
          { label: 'Personas', value: totalPersonas, color: 'text-verde-500' },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-contorno-var bg-white p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-neutro-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filtros + buscador */}
      <div className="rounded-2xl border border-contorno-var bg-white p-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {FILTROS.map((f) => (
            <button
              key={f.valor || 'todas'}
              className={`rounded-full px-3.5 py-1.5 font-semibold transition-colors ${
                filtro === f.valor ? 'bg-verde-400 text-white shadow-sm' : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
              }`}
              onClick={() => setFiltro(f.valor)}
            >
              {f.etiqueta}
            </button>
          ))}
          <input
            className="campo ml-auto w-full sm:w-64"
            type="search"
            placeholder="Buscar por nombre o ID…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-superficie-200 pt-2 text-sm">
          <span className="text-xs font-bold uppercase tracking-wide text-neutro-400">Fecha:</span>
          {FILTROS_TIEMPO.map((t) => (
            <button
              key={t.valor}
              className={`rounded-full px-3.5 py-1.5 font-semibold transition-colors ${
                filtroTiempo === t.valor ? 'bg-neutro-800 text-white shadow-sm' : 'border border-contorno-var bg-white text-neutro-600 hover:bg-superficie-100'
              }`}
              onClick={() => setFiltroTiempo(t.valor)}
            >
              {t.etiqueta}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {cargando ? (
        <Cargando />
      ) : error ? (
        <ErrorApi mensaje={error} onReintentar={recargar} />
      ) : filtradas.length > 0 ? (
        <ul className="space-y-3">
          {filtradas.map((s) => {
            const chip = chipEstado(s);
            const total = s.convocatorias_total || 0;
            const aceptadas = s.convocatorias_aceptadas || 0;
            const pct = total > 0 ? Math.round((aceptadas / total) * 100) : 0;
            const confirmados = s.personas.filter((p) => p.estado === 'aceptada');
            return (
              <li key={s.id} className="rounded-2xl bg-white shadow-sm border border-contorno-var overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-verde-50 text-verde-400">
                        <Icono d={ICONOS.cubiertos} className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-base font-extrabold text-neutro-800">{s.salon_nombre}</p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-neutro-500">
                          <span className="flex items-center gap-1"><Icono d={ICONOS.calendario} className="h-3.5 w-3.5" />{formatFecha(s.fecha)}</span>
                          <span className="flex items-center gap-1"><Icono d={ICONOS.reloj} className="h-3.5 w-3.5" />{formatoRango(s.hora_inicio, s.hora_fin)}</span>
                        </p>
                        {s.descripcion && <p className="mt-1 text-xs text-neutro-400 line-clamp-1">{s.descripcion}</p>}
                      </div>
                    </div>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-extrabold tracking-wide ${chip.clase}`}>{chip.texto}</span>
                  </div>

                  {/* Barra de avance */}
                  {total > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-neutro-500">Personal confirmado</span>
                        <span className="text-xs font-semibold text-neutro-600">{aceptadas}/{total}</span>
                      </div>
                      <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Quién viene, desplegado */}
                  {confirmados.length > 0 && (
                    <div className="mt-4 border-t border-superficie-200 pt-3">
                      <p className="text-sm font-bold text-neutro-800">Quién viene · {s.salon_nombre}</p>
                      <ul className="mt-1">
                        {confirmados.map((p) => (
                          <li key={p.convocatoria_id} className="flex items-center justify-between border-b border-superficie-200 py-2 last:border-0 text-sm">
                            <span className="text-neutro-700">
                              {p.persona_apellido}, {p.persona_nombre} · {p.categoria_nombre}
                              {p.hora_inicio && p.hora_fin && (
                                <span className="ml-1.5 text-xs font-semibold text-verde-500">{formatoRango(p.hora_inicio, p.hora_fin)}</span>
                              )}
                            </span>
                            <span className="font-bold text-estado-ok">Confirmado</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-xs font-medium text-neutro-300">ID: {idCorto(s.id)}</span>
                    <button
                      className="boton bg-verde-400 text-white hover:bg-verde-500 shadow-sm"
                      onClick={() => navigate(`/cliente/solicitud/${s.id}`)}
                    >
                      <Icono d={ICONOS.ojo} /> Ver detalle
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : activas.length > 0 ? (
        <Vacio texto="Nada con este filtro." />
      ) : (
        <div className="tarjeta text-center py-12">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-superficie-200">
            <svg className="h-7 w-7 text-neutro-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-neutro-600">No tenés solicitudes activas</p>
          <p className="mt-1 text-sm text-neutro-400">Tocá «Nueva solicitud» para pedir personal.</p>
        </div>
      )}
    </div>
  );
}
